import { attachTcfApi } from './tcf-api.js';
import { attachGppApi } from './gpp-api.js';
import { attachConsentModeReflection } from './diagnostics.js';
const DEFAULT_ENDPOINT = '/api/v1/region';
const DEFAULT_TIMEOUT_MS = 1200;
const DEFAULT_FALLBACK_REGION = 'eu-eea';
const DEFAULT_CACHE_TTL_MS = 30 * 60 * 1000;
const AUTO_REGION_VALUES = [
    'eu-eea',
    'uk',
    'us-ca',
    'us-va',
    'us-co',
    'us-ct',
    'us-ut',
    'us-nat',
    'row',
];
const AUTO_REGION_SET = new Set(AUTO_REGION_VALUES);
const SESSION_CACHE_PREFIX = 'cmp.region.auto::';
const isFramework = (value) => value === 'tcf' || value === 'gpp' || value === 'basic';
const isAutoRegion = (value) => typeof value === 'string' && AUTO_REGION_SET.has(value);
const deriveFramework = (region, hint) => {
    if (isFramework(hint))
        return hint;
    if (region === 'eu-eea' || region === 'uk')
        return 'tcf';
    if (region.startsWith('us-'))
        return 'gpp';
    return 'basic';
};
const getSessionStorage = () => {
    try {
        if (typeof window === 'undefined' || !('sessionStorage' in window))
            return null;
        return window.sessionStorage;
    }
    catch {
        return null;
    }
};
const createCacheKey = (siteKey, endpoint) => {
    const trimmedSiteKey = siteKey?.trim();
    if (trimmedSiteKey)
        return `${SESSION_CACHE_PREFIX}${trimmedSiteKey.toLowerCase()}`;
    if (!endpoint)
        return null;
    return `${SESSION_CACHE_PREFIX}${endpoint}`;
};
const readCachedDecision = (key, ttlMs) => {
    if (!key)
        return null;
    const storage = getSessionStorage();
    if (!storage)
        return null;
    try {
        const raw = storage.getItem(key);
        if (!raw)
            return null;
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed.at === 'number' && parsed.value) {
            if (Date.now() - parsed.at <= ttlMs &&
                isAutoRegion(parsed.value.region)) {
                const value = parsed.value;
                if (isAutoRegion(value.region) && isFramework(value.framework)) {
                    return value;
                }
            }
        }
        storage.removeItem(key);
    }
    catch {
        try {
            storage.removeItem(key);
        }
        catch {
            /* noop */
        }
    }
    return null;
};
const writeCachedDecision = (key, decision) => {
    if (!key)
        return;
    const storage = getSessionStorage();
    if (!storage)
        return;
    try {
        const payload = { value: decision, at: Date.now() };
        storage.setItem(key, JSON.stringify(payload));
    }
    catch {
        /* ignore sessionStorage quota errors */
    }
};
const createFrameworkApplier = (core, options) => {
    let tcfAttached = false;
    let gppAttached = false;
    const detachTcfApi = () => {
        if (tcfAttached) {
            const cmpWindow = window;
            delete cmpWindow.__tcfapi;
            tcfAttached = false;
        }
    };
    const detachGppApi = () => {
        if (gppAttached) {
            const cmpWindow = window;
            delete cmpWindow.__gpp;
            delete cmpWindow.__setIsCalifornia;
            delete cmpWindow.__setUsRegion;
            gppAttached = false;
        }
    };
    return (framework, region) => {
        const cmpWindow = window;
        cmpWindow.__cmpRegion = region;
        switch (framework) {
            case 'tcf': {
                detachGppApi();
                if (!tcfAttached) {
                    const gdprApplies = options.gdprApplies ?? true;
                    attachTcfApi(core, { ...options, gdprApplies });
                    tcfAttached = true;
                }
                cmpWindow.__cmpFramework = 'tcf';
                return;
            }
            case 'gpp': {
                detachTcfApi();
                if (!gppAttached) {
                    attachGppApi(core);
                    gppAttached = true;
                }
                cmpWindow.__cmpFramework = 'gpp';
                return;
            }
            default: {
                detachTcfApi();
                detachGppApi();
                cmpWindow.__cmpFramework = 'basic';
            }
        }
    };
};
const fetchRegionDecision = async (endpoint, timeoutMs) => {
    if (typeof fetch !== 'function')
        return null;
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : undefined;
    const timeoutId = controller ? setTimeout(() => controller.abort(), timeoutMs) : undefined;
    try {
        const response = await fetch(endpoint, {
            headers: { Accept: 'application/json' },
            signal: controller?.signal,
        });
        const parseJson = async () => {
            try {
                return await response.json();
            }
            catch {
                return null;
            }
        };
        const maybeFromBody = async () => {
            const body = await parseJson();
            if (!body)
                return null;
            const region = isAutoRegion(body.region) ? body.region : undefined;
            if (!region)
                return null;
            const framework = deriveFramework(region, body.framework);
            return { region, framework };
        };
        if (response.ok) {
            return await maybeFromBody();
        }
        if (response.status === 404) {
            return await maybeFromBody();
        }
        return null;
    }
    catch {
        return null;
    }
    finally {
        if (timeoutId)
            clearTimeout(timeoutId);
    }
};
export function initCmpSdkWeb(core, options = {}) {
    if (typeof window === 'undefined')
        return;
    const resolvedOptions = {
        ...options,
        region: options.region ?? 'auto',
        regionEndpoint: options.regionEndpoint ?? DEFAULT_ENDPOINT,
        regionRequestTimeoutMs: options.regionRequestTimeoutMs ?? DEFAULT_TIMEOUT_MS,
        regionFallback: options.regionFallback ?? DEFAULT_FALLBACK_REGION,
        cacheTtlMs: options.cacheTtlMs ?? DEFAULT_CACHE_TTL_MS,
    };
    const applyFramework = createFrameworkApplier(core, resolvedOptions);
    let lastDecision = null;
    const refreshDebug = () => {
        const cmpWindow = window;
        const debug = {
            ...(cmpWindow.DWConsent?.debug ?? {}),
            regionDecision: () => {
                if (!lastDecision)
                    return null;
                return {
                    region: lastDecision.decision.region,
                    framework: lastDecision.decision.framework,
                    source: lastDecision.source,
                    at: new Date(lastDecision.timestamp).toISOString(),
                };
            },
        };
        cmpWindow.DWConsent = {
            ...(cmpWindow.DWConsent ?? {}),
            debug,
        };
    };
    const applyDecision = (decision, source) => {
        applyFramework(decision.framework, decision.region);
        lastDecision = { decision, source, timestamp: Date.now() };
        refreshDebug();
    };
    const targetRegion = resolvedOptions.region ?? 'auto';
    const endpoint = resolvedOptions.regionEndpoint ?? DEFAULT_ENDPOINT;
    const cacheKey = targetRegion === 'auto' ? createCacheKey(resolvedOptions.siteKey, endpoint) : null;
    const cachedDecision = targetRegion === 'auto'
        ? (readCachedDecision(cacheKey, resolvedOptions.cacheTtlMs ?? DEFAULT_CACHE_TTL_MS) ?? null)
        : null;
    if (targetRegion !== 'auto') {
        const forcedRegion = targetRegion;
        applyDecision({ region: forcedRegion, framework: deriveFramework(forcedRegion) }, 'static');
    }
    else {
        if (cachedDecision) {
            applyDecision(cachedDecision, 'cache');
        }
        else {
            const fallbackDecision = {
                region: resolvedOptions.regionFallback ?? DEFAULT_FALLBACK_REGION,
                framework: deriveFramework(resolvedOptions.regionFallback ?? DEFAULT_FALLBACK_REGION),
            };
            applyDecision(fallbackDecision, 'fallback');
            const timeoutMs = resolvedOptions.regionRequestTimeoutMs ?? DEFAULT_TIMEOUT_MS;
            void fetchRegionDecision(endpoint, timeoutMs).then((decision) => {
                if (!decision)
                    return;
                applyDecision(decision, 'network');
                writeCachedDecision(cacheKey, decision);
            });
        }
    }
    attachConsentModeReflection(core);
}
//# sourceMappingURL=init.js.map